/**
 * 時間制の席料（コンカフェ）の計算。ここが唯一の出どころ。
 *
 * なぜ専用に作るか:
 *   夜職の延長料金（extensionPricing.ts）は「1時間いくら」を階段で割る作り。
 *   コンカフェは「30分いくら」を単位で数え、しかも時間帯で単価が変わる。
 *   例: 13:00〜18:00 は 30分 1,200円 / 18:00〜25:00 は 30分 1,400円。
 *   同じ計算に混ぜると、どちらかが必ず狂う。
 *
 * 実在の店で確かめた形（数字は店ごとに違うので設定で持つ）:
 *   - 単位は 15 / 20 / 30 / 40 / 60 分と店によって違う
 *   - 時間帯で単価が変わる。日をまたぐ（25:00 など）書き方をする
 *   - 延長は同じ単位。単価は同額のことも、別額のこともある
 *   - サービス料は 5% 〜 15% と幅がある
 *   - 消費税はサービス料を足したあとに掛ける
 *   - 端数は「10円単位で切り上げ」という店がある
 *   - 「1時間以内の滞在は別途 500円」のような短時間の追加がある
 *
 * 参考にした実店舗の表記:
 *   カウンター 40分 3,000円 / 延長40分 3,000円、消費税10%、サービス料15%、
 *   10円単位は切り上げ、自動延長制
 */

/** 単位として認める分。これ以外は設定として受け付けない。 */
export const ALLOWED_UNIT_MINUTES = [15, 20, 30, 40, 60] as const;
export type UnitMinutes = (typeof ALLOWED_UNIT_MINUTES)[number];

/** 端数の丸め単位。10 なら 10円単位で切り上げ。 */
export const ALLOWED_ROUNDING_UNITS = [1, 10, 100] as const;

/**
 * 時間帯ごとの単価。
 *
 * start / end は 'HH:mm'。閉店が翌日にかかる店があるので
 * 25:00 のように 24 を超える書き方を許す（内部では分に直して扱う）。
 */
export interface TimeChargeBand {
  start: string;
  end: string;
  /** 1単位あたりの席料（税抜・サービス料抜き） */
  unitPrice: number;
  /** 延長1単位あたり。省略したら unitPrice と同じ */
  extensionUnitPrice?: number;
}

/**
 * 単価の決め方。業態で違うので選べるようにする。
 *
 *   perUnitBand … 単位ごとに、その単位が始まる時刻の帯で引き直す。
 *                 18時をまたぐと途中から単価が変わる。
 *                 コンカフェで「時間帯で料金が変わる」と書いてある店はこれ。
 *
 *   entryBand   … 入店した時刻の帯で決めて、最後までその単価で通す。
 *                 「入店時間で初回料金が決まる」型。
 *
 *   flat        … 帯を見ずに決め打ちの単価を使う。
 *                 ススキノのキャバクラのように、初回は入店時刻で変わるのに
 *                 延長だけは時間帯にかかわらず一律、という店がある。
 *                 延長にだけ使うことを想定している。
 */
export type PricingMode = 'perUnitBand' | 'entryBand' | 'flat';

export interface TimeChargeSettings {
  unitMinutes: UnitMinutes;
  bands: TimeChargeBand[];
  /** セット（最初の setUnits 個）の単価の決め方 */
  setPricing: PricingMode;
  /** 延長の単価の決め方 */
  extensionPricing: PricingMode;
  /** extensionPricing / setPricing が 'flat' のときに使う単価 */
  flatSetPrice: number;
  flatExtensionPrice: number;
  /** サービス料の率。0.05 なら 5%。0 なら取らない。 */
  serviceChargeRate: number;
  /** 端数の切り上げ単位。10 なら 10円単位。 */
  roundingUnit: number;
  /** 最初の何単位までを「セット」として扱うか。以降は延長単価。 */
  setUnits: number;
  /** これより短い滞在は shortStayFee を足す（分）。0 なら無し。 */
  shortStayMinutes: number;
  /** 短い滞在のときに足す額（税抜） */
  shortStayFee: number;
  /** 席料の下限（税抜）。ミニマムチャージ。0 なら無し。 */
  minimumCharge: number;
}

export const DEFAULT_TIME_CHARGE: TimeChargeSettings = {
  unitMinutes: 30,
  bands: [],
  setPricing: 'perUnitBand',
  extensionPricing: 'perUnitBand',
  flatSetPrice: 0,
  flatExtensionPrice: 0,
  serviceChargeRate: 0,
  roundingUnit: 10,
  setUnits: 1,
  shortStayMinutes: 0,
  shortStayFee: 0,
  minimumCharge: 0,
};

/** 'HH:mm' を「その営業日の0時からの分」に直す。25:30 → 1530。 */
export function toMinutes(hhmm: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) throw new Error(`時刻の書き方が違います: ${JSON.stringify(hhmm)}`);
  const h = Number(m[1]), mi = Number(m[2]);
  if (mi > 59) throw new Error(`分が 59 を超えています: ${hhmm}`);
  return h * 60 + mi;
}

/**
 * ある時刻がどの帯に入るか。
 *
 * 帯をまたぐ滞在があるので、単位ごとに引き直す。
 * 「入店したときの単価で最後まで通す」にすると、
 * 18時をまたいだ客が全部 1,200円のままになる。
 */
export function bandAt(settings: TimeChargeSettings, minuteOfDay: number): TimeChargeBand | null {
  for (const b of settings.bands) {
    const s = toMinutes(b.start), e = toMinutes(b.end);
    if (minuteOfDay >= s && minuteOfDay < e) return b;
  }
  return null;
}

export interface TimeChargeLine {
  /** その単位が始まる時刻（営業日の0時からの分） */
  atMinute: number;
  /** セットか延長か */
  kind: 'set' | 'extension';
  unitPrice: number;
}

export interface TimeChargeResult {
  /** 数えた単位の数 */
  units: number;
  lines: TimeChargeLine[];
  /** 席料の小計（税抜・サービス料抜き） */
  seatCharge: number;
  /** 短い滞在の追加 */
  shortStayFee: number;
  /** ミニマムチャージで底上げした額 */
  minimumTopUp: number;
  /** 席料の合計（税抜・サービス料抜き） */
  subtotal: number;
  serviceCharge: number;
  /** 端数の切り上げで足した額 */
  rounding: number;
  /** 消費税を掛ける前の額 */
  beforeTax: number;
}

/**
 * 滞在から席料を出す。
 *
 * @param startMinute 入店した時刻（営業日の0時からの分）
 * @param stayMinutes 滞在した分
 *
 * 消費税はここでは掛けない。税は taxRate.ts の 1 箇所に任せる。
 * ここで掛けると税率の出どころが 2 つになる。
 */
export function computeTimeCharge(
  settings: TimeChargeSettings,
  startMinute: number,
  stayMinutes: number,
): TimeChargeResult {
  const unit = settings.unitMinutes;
  if (!ALLOWED_UNIT_MINUTES.includes(unit as UnitMinutes)) {
    throw new Error(`単位の分が不正です: ${unit}`);
  }
  if (stayMinutes < 0) throw new Error('滞在の分が負です');

  // 1 分でも滞在したら 1 単位。切り上げる。
  const units = stayMinutes === 0 ? 0 : Math.ceil(stayMinutes / unit);

  const hhmm = (m: number) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;

  /** その単位いくらか。決め方は業態ごとに違う。 */
  const priceFor = (kind: 'set' | 'extension', atMinute: number): number => {
    const mode = kind === 'set' ? settings.setPricing : settings.extensionPricing;
    if (mode === 'flat') {
      return kind === 'set' ? settings.flatSetPrice : settings.flatExtensionPrice;
    }
    // entryBand は入店時刻、perUnitBand はその単位の時刻で引く
    const at = mode === 'entryBand' ? startMinute : atMinute;
    const band = bandAt(settings, at);
    if (!band) {
      // 帯が無い時間に単価は決められない。黙って 0 にすると、
      // 深夜の客だけ席料が付かないまま会計が通る。
      throw new Error(`${hhmm(at)} の単価が決まっていません`);
    }
    return kind === 'set'
      ? band.unitPrice
      : (band.extensionUnitPrice ?? band.unitPrice);
  };

  const lines: TimeChargeLine[] = [];
  for (let i = 0; i < units; i++) {
    const at = startMinute + i * unit;
    const kind: 'set' | 'extension' = i < settings.setUnits ? 'set' : 'extension';
    lines.push({ atMinute: at, kind, unitPrice: priceFor(kind, at) });
  }

  const seatCharge = lines.reduce((s, l) => s + l.unitPrice, 0);

  // 短い滞在の追加。0 分の客には付けない。
  const shortStayFee =
    settings.shortStayMinutes > 0 && stayMinutes > 0 && stayMinutes < settings.shortStayMinutes
      ? settings.shortStayFee
      : 0;

  const beforeMinimum = seatCharge + shortStayFee;
  const minimumTopUp =
    settings.minimumCharge > 0 && beforeMinimum < settings.minimumCharge && stayMinutes > 0
      ? settings.minimumCharge - beforeMinimum
      : 0;

  const subtotal = beforeMinimum + minimumTopUp;
  // サービス料は席料に対して掛ける。1 円未満は切り捨て。
  const serviceCharge = Math.floor(subtotal * settings.serviceChargeRate);

  const beforeRounding = subtotal + serviceCharge;
  const r = settings.roundingUnit > 0 ? settings.roundingUnit : 1;
  const rounded = Math.ceil(beforeRounding / r) * r;

  return {
    units,
    lines,
    seatCharge,
    shortStayFee,
    minimumTopUp,
    subtotal,
    serviceCharge,
    rounding: rounded - beforeRounding,
    beforeTax: rounded,
  };
}

/** 設定が使える形かを確かめる。画面から保存する前に通す。 */
export function validateTimeCharge(s: TimeChargeSettings): string[] {
  const errs: string[] = [];
  if (!ALLOWED_UNIT_MINUTES.includes(s.unitMinutes)) {
    errs.push(`時間の単位は ${ALLOWED_UNIT_MINUTES.join(' / ')} 分のいずれかにしてください`);
  }
  if (!ALLOWED_ROUNDING_UNITS.includes(s.roundingUnit as 1 | 10 | 100)) {
    errs.push(`端数の切り上げは ${ALLOWED_ROUNDING_UNITS.join(' / ')} 円のいずれかにしてください`);
  }
  if (s.serviceChargeRate < 0 || s.serviceChargeRate > 1) {
    errs.push('サービス料の率は 0% 〜 100% の範囲にしてください');
  }
  // 'flat' しか使わない店は帯が要らない
  const needsBands = s.setPricing !== 'flat' || s.extensionPricing !== 'flat';
  if (needsBands && s.bands.length === 0) {
    errs.push('時間帯ごとの料金を1つ以上入れてください');
  }
  if (s.setPricing === 'flat' && !(s.flatSetPrice > 0)) {
    errs.push('セットを一律にするなら、その単価を入れてください');
  }
  if (s.extensionPricing === 'flat' && !(s.flatExtensionPrice > 0)) {
    errs.push('延長を一律にするなら、その単価を入れてください');
  }

  const seen: Array<[number, number, string]> = [];
  for (const b of s.bands) {
    let st: number, en: number;
    try { st = toMinutes(b.start); en = toMinutes(b.end); }
    catch (e) { errs.push(String((e as Error).message)); continue; }
    if (en <= st) errs.push(`${b.start}〜${b.end} は終わりが始まりより前です`);
    if (!(b.unitPrice >= 0)) errs.push(`${b.start}〜${b.end} の単価が数字ではありません`);
    if (b.extensionUnitPrice !== undefined && !(b.extensionUnitPrice >= 0)) {
      errs.push(`${b.start}〜${b.end} の延長単価が数字ではありません`);
    }
    // 帯が重なっていると、どちらの単価か決まらない
    for (const [ps, pe, label] of seen) {
      if (st < pe && ps < en) errs.push(`${label} と ${b.start}〜${b.end} が重なっています`);
    }
    seen.push([st, en, `${b.start}〜${b.end}`]);
  }
  if (s.shortStayMinutes < 0 || s.shortStayFee < 0) errs.push('短い滞在の設定に負の数は使えません');
  if (s.minimumCharge < 0) errs.push('ミニマムチャージに負の数は使えません');
  return errs;
}
